import type {ThemeId, ThemePreference} from "./types";

/**
 * Shared storage key for theme persistence.
 * Web uses localStorage, Native uses AsyncStorage — both use this same key.
 */
export const THEME_STORAGE_KEY = "@project/theme-preference";

/** Listener signature for theme change subscriptions. */
export type ThemeChangeListener = (theme: ThemeId, preference: ThemePreference) => void;

/**
 * Platform-agnostic theme manager contract.
 *
 * Web implements this via WebThemeManager (localStorage + DOM attributes).
 * Native will implement this via NativeThemeManager (AsyncStorage + Uniwind.setTheme).
 *
 * Both platforms share the same storage key, resolution logic, and registry
 * defined in design-system-core, so future native integration only requires
 * implementing this interface.
 */
export interface IThemeManager {
  /**
   * Initialises the theme from persisted storage and current system preference.
   * Must be called once at app startup before the UI renders.
   * On web: synchronous under the hood (localStorage is sync) — no FOUC.
   * On native: genuinely async (AsyncStorage) — await before rendering.
   */
  init(): Promise<void>;

  /** Returns the user-stored preference, which may be 'system'. */
  getPreference(): ThemePreference;

  /** Returns the currently active, resolved ThemeId (never 'system'). */
  getActiveTheme(): ThemeId;

  /**
   * Changes the user preference, persists it, and immediately re-applies the theme.
   * Notifies all subscribers.
   */
  setPreference(preference: ThemePreference): Promise<void>;

  /**
   * Subscribes to theme changes (triggered by setPreference or OS scheme changes).
   * @returns An unsubscribe function — call it to clean up.
   */
  subscribe(listener: ThemeChangeListener): () => void;
}
